import { Stack } from '@mui/material';
import { Chip, WarningIcon } from '@side/pantry';
import { formatPrice } from '@/common/utils/money';
import type { ClientGroup } from '@/views/Buyers/types';
import { BuyerAgreementStatus } from '../../hooks/useBuyerAgreementStatus';

const AGREEMENT_STATUS_LABEL_MAP: Record<
  Exclude<NonNullable<BuyerAgreementStatus>, 'signed' | 'expired'>,
  string
> = {
  partiallySigned: 'Partially Signed',
  notSigned: 'Not Signed',
  notSent: 'Not Sent',
};

/**
 * Determines the correct buyer agreement URL based on agreement data completeness
 */
export function getBuyerAgreementPath(
  buyer: Pick<
    ClientGroup,
    'id' | 'startDate' | 'endDate' | 'commissionValue' | 'commissionType'
  >,
): string {
  const hasCompleteAgreement =
    buyer.startDate !== null &&
    buyer.endDate !== null &&
    buyer.commissionValue !== null &&
    buyer.commissionType !== null;

  return hasCompleteAgreement
    ? `/buyers/${buyer.id}/documents`
    : `/buyers/${buyer.id}/buyer-agreement`;
}

/**
 * Component to display agreement status
 */
export function AgreementStatusDisplay({
  agreementStatus,
}: {
  agreementStatus: BuyerAgreementStatus;
}) {
  if (agreementStatus === BuyerAgreementStatus.EXPIRED) {
    return (
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <WarningIcon fontSize="small" />
        Expired
      </Stack>
    );
  }

  if (agreementStatus === BuyerAgreementStatus.SIGNED) {
    return (
      <Chip color="success" label="Signed" variant="outlined" size="small" />
    );
  }

  return AGREEMENT_STATUS_LABEL_MAP[agreementStatus];
}

/**
 * Formats commission value based on type
 */
export function formatCommission(
  buyer: Pick<ClientGroup, 'commissionType' | 'commissionValue'>,
): string | null {
  if (!buyer.commissionType || !buyer.commissionValue) {
    return null;
  }

  if (buyer.commissionType === 'PERCENTAGE') {
    return `${buyer.commissionValue}%`;
  }

  return formatPrice(buyer.commissionValue, true, false);
}
